import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import Table from '../../components/table'
import Pagination from '../../components/pagination'
import ApplicationsFilter from './filter'
import { BadgeCellRenderer, LogoCellRenderer } from '../../components/table-cell-renderers'
import ImageModal from '../../components/image-modal'
import useTableDataSync from '../../infrastructure/event-bus/hooks/use-table-data-sync'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import EventBus from '../../infrastructure/event-bus/event-bus'
import general from '../../utils/general'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum, ApplicationStatusEnum } from '../../utils/enums'
import useI18n from '../../i18n/useI18n'
import { useIsAdmin, useIsStudent } from '../../utils/hooks/use-user-role'
import StudentDetailModal from '../students/StudentDetailModal'
import AgentInfoModal from '../students/AgentInfoModal'
import ApplicationDetailModal from './ApplicationDetailModal'
import ApplicationEditModal from './ApplicationEditModal'
import ApplicationStatusUpdateModal from './ApplicationStatusUpdateModal'
import ApplicationStatusHistoryModal from './ApplicationStatusHistoryModal'
import StudentApplicationCard from './StudentApplicationCard'

const Applications = () => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const isAdmin = useIsAdmin()
  const isStudent = useIsStudent()
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  })

  // Student detail modal states
  const [isStudentDetailModalOpen, setIsStudentDetailModalOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [isAgentInfoModalOpen, setIsAgentInfoModalOpen] = useState(false)
  const [agentInfoData, setAgentInfoData] = useState(null)

  // Application detail modal states
  const [isApplicationDetailModalOpen, setIsApplicationDetailModalOpen] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState(null)

  // Application edit modal states
  const [isApplicationEditModalOpen, setIsApplicationEditModalOpen] = useState(false)
  const [selectedApplicationForEdit, setSelectedApplicationForEdit] = useState(null)

  // Application status update modal states
  const [isApplicationStatusUpdateModalOpen, setIsApplicationStatusUpdateModalOpen] = useState(false)
  const [selectedApplicationForStatusUpdate, setSelectedApplicationForStatusUpdate] = useState(null)

  // Application status history modal states
  const [isApplicationStatusHistoryModalOpen, setIsApplicationStatusHistoryModalOpen] = useState(false)
  const [selectedApplicationForHistory, setSelectedApplicationForHistory] = useState(null)

  // Image Modal states
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    imageAlt: ''
  })

  // Filter states
  const [filters, setFilters] = useState({
    student_id: '',
    agent_id: '',
    program_id: '',
    status: '',
    year: '',
    period: '',
    start_date: '',
    end_date: ''
  })

  const fetchApplications = (page = 1, filterParams = {}, limit = null, sortParams = null) => {
    setLoading(true)

    // Build query parameters
    const queryParams = {
      page: page,
      limit: limit || pagination.limit
    }

    // Add filters if they have values
    if (filterParams.student_id && filterParams.student_id.trim()) {
      queryParams.student_id = filterParams.student_id.trim()
    }
    if (filterParams.agent_id && filterParams.agent_id.trim()) {
      queryParams.agent_id = filterParams.agent_id.trim()
    }
    if (filterParams.program_id && filterParams.program_id.trim()) {
      queryParams.program_id = filterParams.program_id.trim()
    }
    if (filterParams.status && filterParams.status.trim()) {
      queryParams.status = filterParams.status.trim()
    }
    if (filterParams.year && filterParams.year.trim()) {
      queryParams.year = filterParams.year.trim()
    }
    if (filterParams.period && filterParams.period.trim()) {
      queryParams.period = filterParams.period.trim()
    }
    if (filterParams.start_date && filterParams.start_date.trim()) {
      queryParams.start_date = filterParams.start_date.trim()
    }
    if (filterParams.end_date && filterParams.end_date.trim()) {
      queryParams.end_date = filterParams.end_date.trim()
    }

    // Add sort parameters if provided
    if (sortParams && sortParams.sort && sortParams.order) {
      queryParams.sort = sortParams.sort
      queryParams.order = sortParams.order
    }

    dispatch(
      ApiCallActions.Get({
        url: 'applications',
        query: queryParams,
        onSuccess: ({ data }) => {
          setApplications(data.data || [])
          setPagination({
            total: data.meta?.total || 0,
            page: data.meta?.page || 1,
            limit: data.meta?.limit || 10,
            totalPages: data.meta?.totalPages || 1
          })
          setLoading(false)
        },
        onError: () => {
          setApplications([])
          setLoading(false)
        }
      })
    )
  }

  useEffect(() => {
    fetchApplications(1, filters)
  }, [])

  const handlePageChange = (page) => {
    fetchApplications(page, filters)
  }

  const handleLimitChange = (newLimit) => {
    const updatedPagination = {
      ...pagination,
      limit: newLimit,
      page: 1
    }
    setPagination(updatedPagination)
    fetchApplications(1, filters, newLimit)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleFilterSubmit = (newFilters) => {
    setFilters(newFilters)
    fetchApplications(1, newFilters)
  }

  const handleClearFilters = (clearedFilters) => {
    setFilters(clearedFilters)
    fetchApplications(1, clearedFilters)
  }

  const handleSort = (columnKey, direction) => {
    const sortParams = direction ? { sort: columnKey, order: direction } : null
    fetchApplications(pagination.page, filters, pagination.limit, sortParams)
  }

  const handleOpenStudentDetailModal = (row) => {
    if (row?.student) {
      setSelectedStudent(row.student)
      setIsStudentDetailModalOpen(true)
    }
  }

  const handleCloseStudentDetailModal = () => {
    setIsStudentDetailModalOpen(false)
    setSelectedStudent(null)
  }

  const getAgentNameFromAgentObject = (agent) => {
    if (!agent) return ''
    if (agent.companyName) return agent.companyName
    if (agent.name) return agent.name
    if (agent.fullName) return agent.fullName

    const firstName = agent.firstName || agent?.user?.firstName || ''
    const lastName = agent.lastName || agent?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()

    if (combined) return combined

    return agent.email || agent?.user?.email || ''
  }

  const handleOpenAgentInfoModal = (student) => {
    if (!student) return

    const resolveAgent = (data) => {
      if (!data) return null
      if (data.agent) return data.agent
      if (Array.isArray(data.agents) && data.agents.length > 0) return data.agents[0]
      return null
    }

    const agent = resolveAgent(student)
    const agentName = getAgentNameFromAgentObject(agent)

    setAgentInfoData({ agent, agentName })
    setIsAgentInfoModalOpen(true)
  }

  const handleOpenAgentInfoModalFromRow = (row) => {
    if (!row?.agent) return

    const agent = row.agent
    const agentName = getAgentNameFromAgentObject(agent)

    setAgentInfoData({ agent, agentName })
    setIsAgentInfoModalOpen(true)
  }

  const handleCloseAgentInfoModal = () => {
    setIsAgentInfoModalOpen(false)
    setAgentInfoData(null)
  }

  const handleOpenApplicationDetailModal = (row) => {
    if (row?.id) {
      setSelectedApplication(row)
      setIsApplicationDetailModalOpen(true)
    }
  }

  const handleCloseApplicationDetailModal = () => {
    setIsApplicationDetailModalOpen(false)
    setSelectedApplication(null)
  }

  const handleShowStudentDetailFromApplication = (student) => {
    setIsApplicationDetailModalOpen(false)
    setSelectedStudent(student)
    setIsStudentDetailModalOpen(true)
  }

  const handleShowAgentInfoFromApplication = (agent) => {
    const agentName = getAgentNameFromAgentObject(agent)
    setAgentInfoData({ agent, agentName })
    setIsAgentInfoModalOpen(true)
  }

  const handleOpenApplicationEditModal = (row) => {
    if (row?.id) {
      setSelectedApplicationForEdit(row)
      setIsApplicationEditModalOpen(true)
    }
  }

  const handleCloseApplicationEditModal = () => {
    setIsApplicationEditModalOpen(false)
    setSelectedApplicationForEdit(null)
  }

  const handleOpenApplicationStatusUpdateModal = (row) => {
    if (row?.id) {
      setSelectedApplicationForStatusUpdate(row)
      setIsApplicationStatusUpdateModalOpen(true)
    }
  }

  const handleCloseApplicationStatusUpdateModal = () => {
    setIsApplicationStatusUpdateModalOpen(false)
    setSelectedApplicationForStatusUpdate(null)
  }

  const handleOpenApplicationStatusHistoryModal = (row) => {
    if (row?.id) {
      setSelectedApplicationForHistory(row)
      setIsApplicationStatusHistoryModalOpen(true)
    }
  }

  const handleCloseApplicationStatusHistoryModal = () => {
    setIsApplicationStatusHistoryModalOpen(false)
    setSelectedApplicationForHistory(null)
  }

  const handleApplicationUpdateSuccess = (updatedData) => {
    // Refresh the list
    fetchApplications(pagination.page, filters, pagination.limit)

    // Update detail modal if open
    if (isApplicationDetailModalOpen && selectedApplication?.id === updatedData?.id) {
      setSelectedApplication(updatedData)
    }
  }

  const handleLogoClick = (imageUrl, imageAlt) => {
    if (imageUrl) {
      setImageModal({
        isOpen: true,
        imageUrl: imageUrl,
        imageAlt: imageAlt
      })
    }
  }

  const handleCloseModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      imageAlt: ''
    })
  }

  const handleDeleteClick = async (row) => {
    const applicationName = row?.student?.user
      ? `${row.student.user.firstName || ''} ${row.student.user.lastName || ''}`.trim()
      : row?.program?.name || t('APPLICATIONS_ENTITY')

    const confirmed = await general.confirmDelete(t('APPLICATIONS_ENTITY'), applicationName)

    if (!confirmed) {
      return
    }

    dispatch(
      ApiCallActions.Delete({
        url: `applications/${row.id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          EventBus.sc.onEntityDeleted({
            entityType: 'application',
            entityId: row.id
          })

          general.showDeleteSuccess(t('APPLICATIONS_ENTITY'))
        }
      })
    )
  }

  // Auto-sync table data with entity events
  useTableDataSync(
    'application',
    applications,
    setApplications,
    pagination,
    setPagination,
    (page, filterParams) => fetchApplications(page, filterParams || filters),
    filters
  )

  const tableColumns = [
    {
      key: 'student',
      label: t('APPLICATIONS_TABLE_COLUMNS_STUDENT'),
      sortable: true,
      render: (value, row) => {
        if (!value) return '-'
        const firstName = value?.user?.firstName || ''
        const lastName = value?.user?.lastName || ''
        const fullName = `${firstName} ${lastName}`.trim()
        const displayName = fullName || value?.user?.email || '-'

        return (
          <div className="d-flex align-items-center">
            {row?.student && (
              <div
                className="show-info-btn"

                onClick={() => handleOpenStudentDetailModal(row)}
                title={t('APPLICATIONS_ACTIONS_VIEW_STUDENT_DETAILS')}
              >
                <i className="zmdi zmdi-info"></i>
              </div>
            )}
            <span>{displayName}</span>
          </div>
        )
      }
    },
    {
      key: 'program',
      label: t('APPLICATIONS_TABLE_COLUMNS_PROGRAM'),
      sortable: true,
      render: (value) => value?.name || '-'
    },
    {
      key: 'university',
      label: t('APPLICATIONS_TABLE_COLUMNS_UNIVERSITY'),
      sortable: true,
      render: (value, row) => {
        let university = value
        if (!university && row?.program?.university) {
          university = row.program.university
        }

        if (!university) return '-'

        const universityName = university.name || '-'
        const logoUrl = university.logoUrl || university.logoURL || university.logo

        return (
          <div className="d-flex align-items-center">
            {logoUrl ? (
              <LogoCellRenderer
                imageUrl={logoUrl}
                imageAlt={universityName}
                onClick={handleLogoClick}
                style={{ width: '30px', height: '30px', marginRight: '8px' }}
              />
            ) : null}
            <span>{universityName}</span>
          </div>
        )
      }
    },
    ...(isAdmin ? [{
      key: 'agent',
      label: t('APPLICATIONS_TABLE_COLUMNS_AGENT'),
      sortable: true,
      render: (value, row) => {
        const agent = row?.agent

        if (!agent) return '-'

        const agentName = getAgentNameFromAgentObject(agent)
        const logoUrl = agent.logoUrl || agent.logoURL || agent.logo

        return (
          <div className="d-flex align-items-center">

            {logoUrl ? (
              <LogoCellRenderer
                imageUrl={logoUrl}
                imageAlt={agentName}
                onClick={handleLogoClick}
                style={{ width: '30px', height: '30px', marginRight: '8px' }}
              />
            ) : null}
            <span>{agentName}</span>
            <div
              className="show-info-btn"
              onClick={() => handleOpenAgentInfoModalFromRow(row)}
              title={t('APPLICATIONS_ACTIONS_VIEW_AGENT_DETAILS')}
            >
              <i className="zmdi zmdi-info"></i>
            </div>
          </div>
        )
      }
    }] : []),
    {
      key: 'applicationStatus',
      label: t('APPLICATIONS_TABLE_COLUMNS_STATUS'),
      sortable: true,
      render: (value, row) => {
        // Use applicationStatus from row if available, otherwise fallback to status
        const statusValue = row?.applicationStatus || value || row?.status
        if (!statusValue) return '-'

        const statusMap = {
          [ApplicationStatusEnum.DRAFT]: {
            label: t('APPLICATIONS_STATUS_DRAFT'),
            percentage: 10,
            color: '#6c757d'
          },
          [ApplicationStatusEnum.SUBMITTED]: {
            label: t('APPLICATIONS_STATUS_SUBMITTED'),
            percentage: 25,
            color: '#17a2b8'
          },
          [ApplicationStatusEnum.UNDER_REVIEW]: {
            label: t('APPLICATIONS_STATUS_UNDER_REVIEW'),
            percentage: 50,
            color: '#ffc107'
          },
          [ApplicationStatusEnum.ACCEPTED]: {
            label: t('APPLICATIONS_STATUS_ACCEPTED'),
            percentage: 100,
            color: '#28a745'
          },
          [ApplicationStatusEnum.REJECTED]: {
            label: t('APPLICATIONS_STATUS_REJECTED'),
            percentage: 100,
            color: '#dc3545'
          },
          [ApplicationStatusEnum.WAITLISTED]: {
            label: t('APPLICATIONS_STATUS_WAITLISTED'),
            percentage: 75,
            color: '#ffc107'
          },
          [ApplicationStatusEnum.CANCELLED]: {
            label: t('APPLICATIONS_STATUS_CANCELLED'),
            percentage: 0,
            color: '#6c757d'
          }
        }
        const statusInfo = statusMap[statusValue.toLowerCase()] || {
          label: statusValue,
          percentage: 0,
          color: '#6c757d'
        }

        return (
          <div style={{ minWidth: '120px' }}>
            <div className="progress" style={{ height: '20px', marginBottom: '4px' }}>
              <div
                className="progress-bar"
                role="progressbar"
                aria-valuenow={statusInfo.percentage}
                aria-valuemin="0"
                aria-valuemax="100"
                style={{
                  width: `${statusInfo.percentage}%`,
                  backgroundColor: statusInfo.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#fff'
                }}
              >
                {statusInfo.percentage > 15 ? `${statusInfo.percentage}%` : ''}
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#495057', textAlign: 'center' }}>
              {statusInfo.label}
            </div>
          </div>
        )
      }
    },
    {
      key: 'createdAt',
      label: t('APPLICATIONS_TABLE_COLUMNS_CREATED_AT'),
      sortable: true,
      render: (value) => {
        if (!value) return '-'
        try {
          const date = new Date(value)
          return date.toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })
        } catch {
          return value
        }
      }
    }
  ]

  const renderActions = (row) => {
    return (
      <div className="d-flex align-items-center">
        <button
          className="btn btn-sm btn-info btn-icon btn-round m-r-5"
          onClick={() => handleOpenApplicationDetailModal(row)}
          title={t('APPLICATIONS_ACTIONS_VIEW_DETAIL')}
        >
          <i className="zmdi zmdi-eye"></i>
        </button>
        <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN, UserTypeEnum.AGENT]}>
          <button
            className="btn btn-sm btn-primary btn-icon btn-round m-r-5"
            onClick={() => handleOpenApplicationStatusUpdateModal(row)}
            title={t('APPLICATIONS_STATUS_UPDATE_BUTTON')}
          >
            <i className="zmdi zmdi-labels"></i>
          </button>
        </RoleBasedAccess>
        <button
          className="btn btn-sm btn-outline-secondary btn-icon btn-round m-r-5"
          onClick={() => handleOpenApplicationStatusHistoryModal(row)}
          title={t('APPLICATIONS_STATUS_HISTORY_BUTTON')}
        >
          <i className="zmdi zmdi-rotate-left"></i>
        </button>
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
          <button
            className="btn btn-sm btn-warning btn-icon btn-round m-r-5"
            onClick={() => handleOpenApplicationEditModal(row)}
            title={t('APPLICATIONS_ACTIONS_EDIT')}
          >
            <i className="zmdi zmdi-edit"></i>
          </button>
          <button
            className="btn btn-sm btn-danger btn-icon btn-round"
            onClick={() => handleDeleteClick(row)}
            title={t('APPLICATIONS_ACTIONS_DELETE')}
          >
            <i className="zmdi zmdi-delete"></i>
          </button>
        </RoleBasedAccess>
      </div>
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>{t('APPLICATIONS_LIST_TITLE')}
              <small>{t('APPLICATIONS_LIST_SUBTITLE')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                </a>
              </li>
              <li className="breadcrumb-item active">{t('APPLICATIONS_LIST_TITLE')}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            {/* Filter Card - Only show for non-students */}
            {!isStudent && (
              <ApplicationsFilter
                filters={filters}
                onFilterChange={handleFilterChange}
                onFilterSubmit={handleFilterSubmit}
                onClearFilters={handleClearFilters}
                onRefresh={() => fetchApplications(pagination.page, filters, pagination.limit)}
                loading={loading}
              />
            )}

            {/* Applications View */}
            {isStudent ? (
              // Student View: Cards
              <div className="row" style={{ marginTop: '30px', minHeight: '60vh' }}>
                {loading ? (
                  <div className="col-12 text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">{t('COMMON_LOADING')}</span>
                    </div>
                  </div>
                ) : applications.length > 0 ? (
                  applications.map((app) => (
                    <StudentApplicationCard
                      key={app.id}
                      application={app}
                      onDetail={handleOpenApplicationDetailModal}
                      onHistory={handleOpenApplicationStatusHistoryModal}
                      onLogoClick={handleLogoClick}
                    />
                  ))
                ) : (
                  <div className="col-12">
                    <div className="card">
                      <div className="body text-center">
                        <p>{t('APPLICATIONS_TABLE_EMPTY')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Admin/Agent View: Table
              <Table
                columns={tableColumns}
                data={applications}
                loading={loading}
                emptyMessage={t('APPLICATIONS_TABLE_EMPTY')}
                renderActions={renderActions}
                onSort={handleSort}
                pagination={pagination}
              />
            )}

            {/* Pagination Card */}
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />

            {/* Student Detail Modal */}
            <StudentDetailModal
              isOpen={isStudentDetailModalOpen}
              student={selectedStudent}
              onClose={handleCloseStudentDetailModal}
              onShowAgentInfo={handleOpenAgentInfoModal}
            />

            {/* Agent Info Modal */}
            <AgentInfoModal
              isOpen={isAgentInfoModalOpen}
              agent={agentInfoData?.agent}
              agentName={agentInfoData?.agentName}
              onClose={handleCloseAgentInfoModal}
            />

            {/* Application Detail Modal */}
            <ApplicationDetailModal
              isOpen={isApplicationDetailModalOpen}
              application={selectedApplication}
              onClose={handleCloseApplicationDetailModal}
              onShowStudentDetail={handleShowStudentDetailFromApplication}
              onShowAgentInfo={handleShowAgentInfoFromApplication}
            />

            {/* Application Edit Modal */}
            <ApplicationEditModal
              isOpen={isApplicationEditModalOpen}
              application={selectedApplicationForEdit}
              onClose={handleCloseApplicationEditModal}
              onSuccess={handleApplicationUpdateSuccess}
            />

            {/* Application Status Update Modal */}
            <ApplicationStatusUpdateModal
              isOpen={isApplicationStatusUpdateModalOpen}
              application={selectedApplicationForStatusUpdate}
              onClose={handleCloseApplicationStatusUpdateModal}
              onSuccess={handleApplicationUpdateSuccess}
            />

            {/* Application Status History Modal */}
            <ApplicationStatusHistoryModal
              isOpen={isApplicationStatusHistoryModalOpen}
              application={selectedApplicationForHistory}
              onClose={handleCloseApplicationStatusHistoryModal}
            />

            {/* Image Modal */}
            <ImageModal
              isOpen={imageModal.isOpen}
              imageUrl={imageModal.imageUrl}
              imageAlt={imageModal.imageAlt}
              onClose={handleCloseModal}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Applications
